import { AutoplayErrorDialog, GenericMessageDialog } from 'uicomponents';

import { getAgoraRTC, getDenoiserExtension } from './agoraLoader';
import { cancelAudioRecovery, scheduleAudioRecovery } from './audioRecovery.service';
import {
  getCloudProxyModeIndex,
  getOrgCloudProxyMode,
  handlePeerConnectionTimeout,
  verifyAndSaveProxyMode
} from './cloudProxy.service';
import { classifyDiagnosticError } from './diagnostics';
import DialogService from './dialogs/DialogService';
import NotificationService from './dialogs/notifications';
import { fetchTurnServerCredentials, getAgoraTokenWithRetries, syncUserUIDsWithBackend } from './liveMediaAPI.service';
import { getValueFromSessionStorage, saveValueToSessionStorage } from './localStorage.service';
import loggerService from './logger.service';
import { getTrackingValues, setUsingCloudProxy } from './mediaTracking.utils';
import { currentUserHasStageRequest, updateOnStageRequest } from './stageControl.service';
import { getAudioTrackSnapshots, LocalTrack, trackMap } from './stream.service';
import trackerService from './tracker.service';
import { UserRole } from './user.service';
import {
  getActiveSpeakerUpdate,
  MediaUserRole,
  type MediaRoom,
  updateSDRecordingStatus,
  wrapClientForMock
} from './utils/liveMedia.utils';

import type {
  IAgoraRTCError,
  ConnectionDisconnectedReason,
  ConnectionState,
  IAgoraRTCClient,
  IAgoraRTCRemoteUser,
  ILocalTrack,
  ITrack,
  NetworkQuality,
  SDK_MODE
} from './agora.types';

import analytics from '@/analytics';
import { AGORA, AGORA_CONNECTION_TIMEOUT, AGORA_EVENTS, LOCAL_STORAGE_KEYS } from '@/constants';
import store from '@/store';
import { Broadcast, User } from '@/store/modules/event/types';
import { ProgressiveCloudProxyStatus, TurnServerFallbackStatus } from '@/store/modules/mediaroom/types';
import { collectNetworkQualityDataPoints } from '@/utils/networkQuality';
import JoiningDelayDialog from '@/views/event/TechCheck/JoiningDelayDailog.vue';

export {
  getUserSuffixForAgoraToken,
  isAnyMediaRoomReconnecting,
  MediaUserRole,
  wrapClientForMock
} from './utils/liveMedia.utils';
export type { AgoraToken, MediaRoom, RemoteMediaStream, RtcDevices } from './utils/liveMedia.utils';
export { fetchAgoraToken, getAgoraTokenToJoinClient } from './liveMediaAPI.service';

type ShouldSubscribeToRemoteUser = (uid: string, mediaType?: string) => boolean;

type ActiveSpeakerState = {
  lastActiveSpeakerId: string | null;
  lastSpokenAt: number;
};

type AgoraVolumeIndicator = {
  uid: string | number;
  level: number;
};

type AgoraException = {
  code: number;
  msg: string;
  uid?: string | number;
};

const NOTIFICATIONS_THRESHOLD = 3;
const failingSubscriptionUids = new Set<string>();
const REMOTE_USER_SUBSCRIBE_MAX_ATTEMPTS = 5;
const RETRY_BASE_DELAY = 333;
const peerConnectionTimeouts = new Map<string, NodeJS.Timeout>();
const peerConnectionStartTimes = new Map<string, number>();
let agoraDisconnectionCount = 0;
let noOfLowAudioNotifications = 0;
let noOfSendLowBitrateNotifications = 0;
let agoraHandlersInitialized = false;
let clientJoinRetries = 0;
let remoteSignalTrackerInterval: NodeJS.Timeout | null = null;

/* Agora setup and media-room creation */

/**
 * Initializes Agora global handlers. Called lazily when Agora SDK is first loaded.
 */
async function initializeAgoraHandlers() {
  if (agoraHandlersInitialized) {
    return;
  }

  const AgoraRTC = await getAgoraRTC();
  AgoraRTC.onAutoplayFailed = () => {
    store.commit('event/stage/toggleMuted');
    const res = DialogService.show(AutoplayErrorDialog);
    res.promise.then(() => {
      store.commit('event/stage/toggleMuted');
    });
  };

  // Update noise cancellation compatibility now that SDK is loaded
  const denoiser = getDenoiserExtension();
  if (denoiser) {
    store.commit('mediaroom/setNoiseCancellationCompatible', denoiser.checkCompatibility());
  }

  agoraHandlersInitialized = true;
}

export async function enableAgoraLogs() {
  const AgoraRTC = await getAgoraRTC();
  AgoraRTC.enableLogUpload();
  AgoraRTC.setLogLevel(0);
}

export async function createMediaRoom(
  channelName: MediaRoom['channelName'],
  userRole: MediaUserRole,
  mode: SDK_MODE
): Promise<MediaRoom> {
  // Load Agora SDK lazily and initialize handlers
  const AgoraRTC = await getAgoraRTC();
  await initializeAgoraHandlers();

  // Enable Agora logs (always enabled)
  await enableAgoraLogs();

  // If we want to change h264, do check screen share tab on Chrome, it takes a long time to load screen
  const codec = 'vp8';
  let client = AgoraRTC.createClient({
    mode,
    codec
  });
  let screenShareClient = AgoraRTC.createClient({
    mode,
    codec
  });

  if (AGORA.APP_ID === 'mock-agora') {
    client = wrapClientForMock(client);
    screenShareClient = wrapClientForMock(screenShareClient);
  }

  return {
    client,
    screenShareClient,
    mode,
    channelName,
    remoteStreams: {},
    isMuted: false,
    activeSpeakerId: '',
    speakerOutDeviceId: '',
    connectionStrength: 0,
    userRole,
    id: new Date().getTime()
  };
}

/**
 * Sets up Cloudflare TURN servers as a fallback when proxy is exhausted
 * @param client - Agora RTC client to configure
 */
export async function setupTurnServerFallback(
  client: IAgoraRTCClient,
  channelName: string,
  preferredTurnServerUrl: string
) {
  try {
    if (preferredTurnServerUrl) {
      loggerService.log('[AGORA] Using preferred turn server url', preferredTurnServerUrl);
    } else {
      loggerService.log('[AGORA] Setting up TURN server fallback after proxy exhaustion');
    }

    const routeName = store.state.route.name;
    /*
    This will return two arrays with first being URLs for STUN servers and second array with URLs for TURN servers
    STUN servers help with NAT traversal, TURN servers relay traffic when direct connection fails
    */
    const response = await fetchTurnServerCredentials(routeName === 'UngatedTechCheck');

    if (!response) {
      throw new Error(`Failed to fetch TURN credentials`);
    }
    const cloudflrareTurnUrl = preferredTurnServerUrl || response.ice_servers[1].urls.pop();

    store.commit('mediaroom/updateChannelPeerConnectionState', {
      channelName: channelName,
      updates: {
        turnServerUrl: cloudflrareTurnUrl
      }
    });
    loggerService.log('[AGORA] TURN server credentials obtained using turn server fallback', {
      turnServerUrl: cloudflrareTurnUrl
    });

    client.setTurnServer([
      {
        urls: response.ice_servers[0].urls
      },
      {
        urls: cloudflrareTurnUrl,
        username: response.ice_servers[1].username,
        credential: response.ice_servers[1].credential
      }
    ] as any);
  } catch (error) {
    loggerService.error('[AGORA] Failed to setup TURN server fallback', { error });
    store.commit('mediaroom/updateChannelPeerConnectionState', {
      channelName: channelName,
      updates: {
        turnServerFallbackStatus: TurnServerFallbackStatus.FAILED,
        turnServerUrl: undefined
      }
    });
    throw error;
  }
}
/* Agora event handlers */

function shouldSubscribeToRemoteUser(currentUserId: string | undefined, uid: string, mediaType?: string): boolean {
  if (store.state.event.stage?.isLiveStreamMode) {
    return false;
  }
  if (
    store.state.event.stage?.isTVMode &&
    uid.includes(AGORA.UID_SUFFIX.LIVE_STREAMER) &&
    uid.includes(currentUserId || '')
  ) {
    return false;
  }

  if (
    mediaType === 'audio' &&
    uid.includes(AGORA.UID_SUFFIX.SCREEN_STREAM) &&
    uid.includes(store.state.user.currentUser?.id || '')
  ) {
    // Don't play the audio locally for the user who is sharing
    return false;
  }
  return true;
}

async function handleScreenShareConnectionStateChange(
  mediaRoom: MediaRoom,
  currentState: ConnectionState,
  previousState: ConnectionState,
  connectionDisconnectedReason?: ConnectionDisconnectedReason
) {
  loggerService.warn(`agora-screen-client-connection-state ${previousState} -> ${currentState}`);

  if (currentState === 'DISCONNECTED') {
    if (connectionDisconnectedReason != 'LEAVE') {
      trackerService.track('agora', 'disconnections');
    }

    loggerService.warn('Agora screen client connection disconnected', {
      connectionDisconnectedReason
    });
  }

  try {
    const response = await store.dispatch('mediaroom/handleConnectionStateChangeForScreen', {
      currentState,
      previousState,
      channelName: mediaRoom.channelName,
      connectionDisconnectedReason
    });
    updateSDRecordingStatus('screenshare', mediaRoom.channelName, true, connectionDisconnectedReason);
    return response;
  } catch (error) {
    loggerService.error('Error handling screen connection state change', error);
  }
}

function handlePeerConnectionStateChange(
  mediaRoom: MediaRoom,
  currentPeerConnectionState: RTCPeerConnectionState,
  previousPeerConnectionState: RTCPeerConnectionState
) {
  const currentState = store.state.mediaroom.mediaRooms[mediaRoom.channelName]?.status;
  loggerService.warn(`agora-peer-connection-state ${previousPeerConnectionState} -> ${currentPeerConnectionState}`, {
    channelName: mediaRoom.channelName
  });
  store.commit('mediaroom/updateChannelPeerConnectionState', {
    channelName: mediaRoom.channelName,
    updates: {
      peerConnectionState: currentPeerConnectionState
    }
  });

  updateSDRecordingStatus('user', mediaRoom.channelName, false);

  if (currentPeerConnectionState !== 'connected') {
    return;
  }

  const channelKey = mediaRoom.channelName;
  const timeout = peerConnectionTimeouts.get(channelKey);

  if (timeout) {
    clearTimeout(timeout);
    peerConnectionTimeouts.delete(channelKey);
  }

  verifyAndSaveProxyMode(mediaRoom.channelName);
  const startTime = peerConnectionStartTimes.get(channelKey);
  const timeToSuccess = startTime ? Date.now() - startTime : null;
  if (timeToSuccess !== null) {
    peerConnectionStartTimes.delete(channelKey);
  }
  if (store.state.event.useCloudProxyFlow) {
    analytics.track('agora_peer_connection_success', {
      ...getTrackingValues(mediaRoom),
      connectionStatus: currentState,
      peerConnectionStatus: currentPeerConnectionState,
      timeToSuccessMs: timeToSuccess
    });
  }
}

async function handleMediaRoomConnectionStateChange(
  mediaRoom: MediaRoom,
  currentUserId: string | undefined,
  currentState: ConnectionState,
  previousState: ConnectionState,
  connectionDisconnectedReason?: ConnectionDisconnectedReason
) {
  loggerService.warn(`agora-client-connection-state ${previousState} -> ${currentState}`);
  store.commit('mediaroom/updateChannelPeerConnectionState', {
    channelName: mediaRoom.channelName,
    updates: {
      status: currentState
    }
  });

  const currentBroadcast: Broadcast = store.getters['event/currentBroadcast'];

  if (currentState === 'DISCONNECTED') {
    if (connectionDisconnectedReason != 'LEAVE') {
      trackerService.track('agora', 'disconnections');
      agoraDisconnectionCount++;
    }

    analytics.track('agora-disconnected', {
      user_id: currentUserId,
      broadcast_id: currentBroadcast?.id,
      discussion_room_id: store.state.route.name == 'DiscussionRoom' ? store.state.route.params?.id : '',
      event_id: store.state.event.event?.id,
      reason_for_disconnect: connectionDisconnectedReason,
      agora_user_role: mediaRoom.userRole,
      event_role: store.state.user.currentEventUser?.event_role
    });
    loggerService.warn('Agora client connection disconnected', {
      connectionDisconnectedReason
    });
  }

  if (currentState === 'CONNECTED') {
    const channelKey = mediaRoom.channelName;

    const existing = peerConnectionTimeouts.get(channelKey);
    if (existing) {
      clearTimeout(existing);
      peerConnectionTimeouts.delete(channelKey);
    }

    const timeout = setTimeout(() => {
      const latestPeerConnectionState =
        store.state.mediaroom.channelPeerConnectionStates[channelKey]?.peerConnectionState || 'new';

      if (latestPeerConnectionState === 'connected') {
        peerConnectionTimeouts.delete(channelKey);
        return;
      }

      handlePeerConnectionTimeout(channelKey);
      peerConnectionTimeouts.delete(channelKey);
    }, AGORA_CONNECTION_TIMEOUT);

    peerConnectionTimeouts.set(channelKey, timeout);
  }

  try {
    const response = await store.dispatch('mediaroom/handleConnectionStateChange', {
      currentState,
      previousState,
      channelName: mediaRoom.channelName,
      connectionDisconnectedReason
    });
    updateSDRecordingStatus('user', mediaRoom.channelName, false, connectionDisconnectedReason);
    return response;
  } catch (error) {
    loggerService.error('Error handling connection state change', error);
  }
}

async function handleRemoteUserPublished(
  mediaRoom: MediaRoom,
  shouldSubscribe: ShouldSubscribeToRemoteUser,
  user: IAgoraRTCRemoteUser,
  mediaType: ITrack['trackMediaType']
) {
  if (!shouldSubscribe(String(user.uid), mediaType)) {
    return;
  }
  if (mediaType === 'video' && mediaRoom.channelName.includes(AGORA.UID_SUFFIX.BACKSTAGE)) {
    if (store.state.event.stage?.greenRoomVideoMuted) {
      return false;
    }
  }

  let currentSubscribeAttempt = 0;
  const remoteUserId = String(user.uid).split('|')[0];
  while (currentSubscribeAttempt < REMOTE_USER_SUBSCRIBE_MAX_ATTEMPTS) {
    try {
      await mediaRoom.client.subscribe(user, mediaType);

      // On success, remove from failing list to prevent future false-positive notifications.
      if (failingSubscriptionUids.has(remoteUserId)) {
        failingSubscriptionUids.delete(remoteUserId);
      }

      return store.dispatch('mediaroom/addRemoteUserTrack', {
        user,
        mediaType,
        channelName: mediaRoom.channelName
      });
    } catch (err) {
      const rtcError = err as IAgoraRTCError;
      const ignoredErrors = ['INVALID_REMOTE_USER', 'UNEXPECTED_RESPONSE'];

      if (ignoredErrors.includes(rtcError.code)) {
        loggerService.warn('Not a valid user, skipping subscribe attempt');
        return;
      }
      currentSubscribeAttempt++;
      loggerService.warn(`Attempt ${currentSubscribeAttempt} to subscribe to ${user.uid} failed.`, err);

      // Stop retrying after last attempt
      if (currentSubscribeAttempt >= REMOTE_USER_SUBSCRIBE_MAX_ATTEMPTS) {
        loggerService.error(
          `Max subscribe retries reached, rejoining room ${mediaRoom.channelName} for user ${user.uid}`
        );

        // Check if main stage user is trying to subscribe to backstage user - skip rejoin in this case
        const currentChannelIsBackstage = mediaRoom.channelName.includes(AGORA.UID_SUFFIX.BACKSTAGE);
        const mainStageChannelId = Array.isArray(store.state.route.params?.id)
          ? store.state.route.params.id[0]
          : store.state.route.params?.id;
        const mainStageRoom = store.state.mediaroom.mediaRooms[mainStageChannelId];
        const isCurrentUserMainStageUser = mainStageRoom?.userRole === MediaUserRole.host;

        const shouldSkipRejoin = isCurrentUserMainStageUser && currentChannelIsBackstage;

        if (failingSubscriptionUids.has(remoteUserId)) {
          // This is the second failure (after rejoining), show notification and stop.
          failingSubscriptionUids.delete(remoteUserId);
          loggerService.error(`Still failing to subscribe to ${user.uid} after rejoining.`);
          if (mediaRoom.userRole === MediaUserRole.audience) {
            NotificationService.toast(
              '@@translate@@ Connection Issue',
              '@@translate@@ We are unable to connect you to the stream. Please refresh the page or switch to the lite tab.',
              'warning'
            );
          } else {
            NotificationService.toast(
              '@@translate@@ Connection Issue',
              '@@translate@@ We are unable to connect you to the stream. Please refresh the page',
              'warning'
            );
          }
        } else if (shouldSkipRejoin) {
          failingSubscriptionUids.add(remoteUserId);
          loggerService.warn(
            `Main stage user failed to subscribe to backstage user ${user.uid}. Skipping room rejoin to avoid disrupting main stage.`
          );
          NotificationService.toast(
            '@@translate@@ Connection Issue',
            '@@translate@@ Unable to connect to backstage stream, please refresh the page',
            'warning'
          );
        } else {
          // First time hitting max failures, add to list and rejoin room to recover silently.
          failingSubscriptionUids.add(remoteUserId);
          loggerService.warn(
            `Max subscribe retries reached for ${user.uid}. Rejoining room ${mediaRoom.channelName} to recover.`
          );
          await store.dispatch('mediaroom/exitRoom', { channelName: mediaRoom.channelName });
          await store.dispatch('mediaroom/joinRoom', {
            channelName: mediaRoom.channelName,
            role: mediaRoom.userRole,
            mode: mediaRoom.mode,
            forceFetchFromApi: true
          });
          return;
        }
      }
      const backoff = Math.pow(2, currentSubscribeAttempt) * RETRY_BASE_DELAY;
      await new Promise(resolve => setTimeout(resolve, backoff));
    }
  }
}

function handleRemoteUserUnpublished(
  mediaRoom: MediaRoom,
  shouldSubscribe: ShouldSubscribeToRemoteUser,
  user: IAgoraRTCRemoteUser,
  mediaType: ITrack['trackMediaType']
) {
  if (!shouldSubscribe(String(user.uid), mediaType)) {
    return;
  }
  return store.dispatch('mediaroom/removeRemoteUserTrack', {
    user,
    mediaType,
    channelName: mediaRoom.channelName
  });
}

function handleRemoteUserJoined(
  mediaRoom: MediaRoom,
  shouldSubscribe: ShouldSubscribeToRemoteUser,
  user: IAgoraRTCRemoteUser
) {
  if (!shouldSubscribe(String(user.uid))) {
    return;
  }
  // Disable Fallback to Audio Only streams under low network (can enable for DRs)
  mediaRoom.client.setStreamFallbackOption(user.uid, 0);
  return store.dispatch('mediaroom/addRemoteUser', {
    user,
    channelName: mediaRoom.channelName
  });
}

function handleRemoteUserLeft(
  mediaRoom: MediaRoom,
  shouldSubscribe: ShouldSubscribeToRemoteUser,
  user: IAgoraRTCRemoteUser
) {
  if (!shouldSubscribe(String(user.uid))) {
    return;
  }
  return store.dispatch('mediaroom/removeRemoteUser', {
    user,
    channelName: mediaRoom.channelName
  });
}

function handleNetworkQuality(networkQuality: NetworkQuality) {
  collectNetworkQualityDataPoints(networkQuality);
  return store.dispatch('mediaroom/checkAndUpdateLocalNetworkQuality', {
    networkQuality
  });
}

function handleJoinFallbackToProxy(proxyServer: unknown) {
  loggerService.warn('Fallback to proxy server', proxyServer);
}

function handleUsingCloudProxy(isUsingCloudProxy: boolean) {
  setUsingCloudProxy(isUsingCloudProxy);
  loggerService.warn('Using cloud proxy', isUsingCloudProxy);
}

function handleVolumeIndicator(
  mediaRoom: MediaRoom,
  currentUserId: string | undefined,
  activeSpeakerState: ActiveSpeakerState,
  volumes: AgoraVolumeIndicator[]
) {
  const now = Date.now();
  const activeSpeakerUpdate = getActiveSpeakerUpdate({
    volumes,
    localTracks: store.state.mediaroom.localTracks,
    localAudioLevel: store.state.mediaroom.localAudioLevel,
    currentUserId,
    lastActiveSpeakerId: activeSpeakerState.lastActiveSpeakerId,
    lastSpokenAt: activeSpeakerState.lastSpokenAt,
    now
  });
  activeSpeakerState.lastActiveSpeakerId =
    activeSpeakerUpdate.activeSpeakerId || activeSpeakerState.lastActiveSpeakerId;
  activeSpeakerState.lastSpokenAt = activeSpeakerUpdate.lastSpokenAt;

  loggerService.info('Active speaker detection', {
    detectedSpeaker: activeSpeakerUpdate.detectedSpeaker,
    channelName: mediaRoom.channelName,
    maxRemoteAudioLevel: activeSpeakerUpdate.maxRemoteAudioLevel,
    localAudioLevel: store.state.mediaroom.localAudioLevel,
    lastActiveSpeakerId: activeSpeakerState.lastActiveSpeakerId
  });

  if (activeSpeakerUpdate.activeSpeakerId) {
    store.commit('mediaroom/setActiveSpeakerId', {
      channelName: mediaRoom.channelName,
      activeSpeakerId: activeSpeakerUpdate.activeSpeakerId
    });
  }
}

function handleMediaRoomException(mediaRoom: MediaRoom, currentUserId: string | undefined, exception: AgoraException) {
  trackerService.track('agora', 'error');
  loggerService.warn(`Agora client exception ${exception.code} ${exception.msg}`, { uid: exception.uid });

  if (String(exception.uid).includes(currentUserId || '')) {
    handleException(
      mediaRoom,
      exception.code,
      exception.uid as string,
      (mediaRoom.client as any)._cloudProxyServerMode === 'enabled'
    );
  }
}

function handleScreenShareException(exception: AgoraException) {
  trackerService.track('agora', 'error');
  loggerService.warn(`Agora screen client exception ${exception.code} ${exception.msg}`, { uid: exception.uid });
}

/* Room lifecycle and public media actions */

export function closeJoiningDelayNetworkErrorModal() {
  const dialogReferenceIndex = store.state.mediaroom.networkErrorIndex;
  if (dialogReferenceIndex !== undefined) {
    DialogService.close(dialogReferenceIndex);
    store.commit('mediaroom/setNetworkErrorIndex', undefined);
  }
}

async function handleJoiningDelay(channelName: string) {
  const channelStorageKey = `${LOCAL_STORAGE_KEYS.hasShownJoiningDelayNetworkErrorModal}_${channelName}`;
  if (getValueFromSessionStorage(channelStorageKey)) {
    loggerService.info('Joining delay network error modal has already been shown for channel', { channelName });
    return;
  }
  saveValueToSessionStorage(channelStorageKey, true);
  const isAttendee = store.state.user.currentEventUser?.event_role === UserRole.EVENT_MEMBER;
  const isTechCheck = ['SpeakerTechSetup', 'AttendeeTechSetup', 'UngatedTechCheck'].includes(
    <string>(store.state.route.name || '')
  );
  closeJoiningDelayNetworkErrorModal();
  analytics.track('joining_delay_network_error_modal', {
    isAttendee: isAttendee,
    isTechCheck: isTechCheck
  });
  const { index } = DialogService.show(JoiningDelayDialog, {
    size: 'small',
    isTechCheck: isTechCheck,
    isAttendee: isAttendee
  });
  store.commit('mediaroom/setNetworkErrorIndex', index);
}

async function handleJoinChannel(mediaRoom: MediaRoom, isScreen?: boolean, forceFetchTokenFromApi: Boolean = false) {
  const { channelName, id } = mediaRoom;
  const currentUserId = store.state.user.currentUser?.id;

  if (!currentUserId) {
    loggerService.warn('handleJoinChannel: currentUserId is undefined');
    return;
  }

  const userRole = isScreen ? MediaUserRole.host : mediaRoom.userRole;
  const client = isScreen ? mediaRoom.screenShareClient : mediaRoom.client;

  const progressiveCloudProxyMode =
    store.state.mediaroom.channelPeerConnectionStates?.[channelName]?.progressiveCloudProxyMode;
  const preferredCloudProxyMode = parseInt(getValueFromSessionStorage(LOCAL_STORAGE_KEYS.preferredCloudProxyMode));
  const isTurnServerFallbackInProgress =
    store.state.mediaroom.channelPeerConnectionStates?.[channelName]?.turnServerFallbackStatus === 'IN_PROGRESS';
  const preferredTurnServerUrl = getValueFromSessionStorage(LOCAL_STORAGE_KEYS.preferredTurnServerUrl);
  const orgCloudProxyMode = getOrgCloudProxyMode();

  if (progressiveCloudProxyMode !== undefined && progressiveCloudProxyMode !== 'turn_server_fallback') {
    loggerService.log('[AGORA] Using progressive cloud proxy mode', progressiveCloudProxyMode);
    client.startProxyServer(progressiveCloudProxyMode);
  } else if (isTurnServerFallbackInProgress || preferredTurnServerUrl) {
    try {
      analytics.track('progressive-cloud-proxy-attempt', {
        ...getTrackingValues(mediaRoom),
        channelName,
        reason: 'preferred-cloud-proxy-mode',
        proxyMode: 'turn_server_fallback'
      });
      await setupTurnServerFallback(client, channelName, preferredTurnServerUrl);
    } catch (error) {
      loggerService.error('[AGORA] Failed to setup TURN server fallback in handleJoinChannel', { error });
    }
  } else if (preferredCloudProxyMode) {
    store.commit('mediaroom/updateChannelPeerConnectionState', {
      channelName,
      updates: {
        progressiveCloudProxyMode: preferredCloudProxyMode,
        isProgressiveCloudProxyStatus: ProgressiveCloudProxyStatus.IN_PROGRESS,
        currentProxyModeIndex: getCloudProxyModeIndex(preferredCloudProxyMode)
      }
    });
    analytics.track('progressive-cloud-proxy-attempt', {
      ...getTrackingValues(mediaRoom),
      channelName,
      reason: 'preferred-cloud-proxy-mode',
      proxyMode: preferredCloudProxyMode
    });
    loggerService.warn('[AGORA] Using stored preferred cloud proxy mode', preferredCloudProxyMode);
    client.startProxyServer(preferredCloudProxyMode);
  } else if (orgCloudProxyMode !== undefined) {
    // Start the progressive chain at the org's mode; a peer connection timeout falls through to the next mode.
    store.commit('mediaroom/updateChannelPeerConnectionState', {
      channelName,
      updates: {
        progressiveCloudProxyMode: orgCloudProxyMode,
        isProgressiveCloudProxyStatus: ProgressiveCloudProxyStatus.IN_PROGRESS,
        currentProxyModeIndex: getCloudProxyModeIndex(orgCloudProxyMode)
      }
    });
    analytics.track('progressive-cloud-proxy-attempt', {
      ...getTrackingValues(mediaRoom),
      channelName,
      reason: 'org-cloud-proxy-mode',
      proxyMode: orgCloudProxyMode
    });
    loggerService.warn('[AGORA] Using organization cloud proxy mode', orgCloudProxyMode);
    client.startProxyServer(orgCloudProxyMode);
  }

  const requiresConnect = () => {
    const channelRequiresConnect =
      store.state.mediaroom.mediaRooms[channelName]?.desiredStatus === 'CONNECTED' &&
      id === store.state.mediaroom.mediaRooms[channelName]?.id;

    if (isScreen) {
      return (
        channelRequiresConnect &&
        store.state.mediaroom.screenTracks.find((track: LocalTrack) => track.channelName === channelName)
      );
    }

    return channelRequiresConnect;
  };

  const isBackstage = channelName.includes(AGORA.UID_SUFFIX.BACKSTAGE);
  const joiningDelayTimeout = setTimeout(
    async () => {
      if (!requiresConnect()) {
        return;
      }

      if (
        ['SpeakerTechSetup', 'AttendeeTechSetup', 'UngatedTechCheck'].includes(<string>(store.state.route.name || ''))
      ) {
        return;
      }

      handleJoiningDelay(channelName);
    },
    isBackstage ? 12000 : 10000
  );

  trackerService.track('agora', 'connectionInitialized', { timestamp: Date.now() });
  try {
    const { token, suffix } = await getAgoraTokenWithRetries(
      channelName,
      userRole === MediaUserRole.audience,
      isScreen,
      forceFetchTokenFromApi
    );

    await client.join(AGORA.APP_ID, channelName, token, currentUserId + suffix);
    analytics.track('join_media_room', {
      ...getTrackingValues(mediaRoom)
    });
    trackerService.track('agora', 'connectionEstablished', { timestamp: Date.now() });

    logAgoraUserMetadata(store.state.user.currentEventUser, userRole, suffix, channelName);

    toggleRemoteSignalTracking({ mediaRoom, enable: true });

    clearTimeout(joiningDelayTimeout);

    closeJoiningDelayNetworkErrorModal();

    if (!requiresConnect()) {
      await client.leave();
      if (id === store.state.mediaroom.mediaRooms[channelName]?.id) {
        await store.dispatch('mediaroom/exitRoom', { channelName });
      }
      return;
    }

    loggerService.warn('agora-join-channel');

    if (userRole === MediaUserRole.host && mediaRoom.mode === AGORA.MODE.LIVE) {
      await setRole(client, channelName, MediaUserRole.host);
    }
    const dispatchAction = isScreen ? 'mediaroom/checkAndPublishScreenToChannel' : 'mediaroom/checkAndPublishToChannel';
    store.dispatch(dispatchAction, { channelName });
  } catch (error) {
    clearTimeout(joiningDelayTimeout);
    analytics.track('join_media_room_failed', {
      ...getTrackingValues(mediaRoom),
      error: error
    });
    if (requiresConnect()) {
      if (clientJoinRetries < 3) {
        trackerService.track('agora', 'retry');
        clientJoinRetries = clientJoinRetries + 1;
        setTimeout(() => {
          handleJoinChannel(mediaRoom, isScreen, true);
        }, 2000);
      } else {
        loggerService.info('[PAGE_RELOAD] Reloading after Agora channel join failed all retries', {
          channelName,
          isScreen,
          clientJoinRetries
        });
        window.location.reload();
      }
    } else {
      closeJoiningDelayNetworkErrorModal();
    }
    loggerService.captureException(error as Error);
  }
}

export function toggleRemoteSignalTracking({ mediaRoom, enable }: { mediaRoom: MediaRoom; enable: boolean }) {
  if (!mediaRoom) {
    return;
  }

  const isBroadcast = store.state.route.path.includes('/broadcast');
  const eventRole = store.state.user.currentEventUser?.event_role as UserRole;

  if ([UserRole.EVENT_MEMBER, UserRole.RECORDER_BOT, UserRole.SPONSOR].includes(eventRole) && isBroadcast) {
    return;
  }

  if (remoteSignalTrackerInterval) {
    clearInterval(remoteSignalTrackerInterval);
    remoteSignalTrackerInterval = null;
    store.commit('mediaroom/setRemoteSignalMetadata', {
      remoteNetworkQuality: {}
    });
  }

  if (enable) {
    remoteSignalTrackerInterval = setInterval(() => {
      const mediaRooms = store.state.mediaroom.mediaRooms;
      const mergedNetworkQuality = {};

      Object.values(mediaRooms).forEach(room => {
        if (room.client?.connectionState === 'CONNECTED') {
          Object.assign(mergedNetworkQuality, room.client.getRemoteNetworkQuality());
        }
      });

      store.commit('mediaroom/setRemoteSignalMetadata', {
        remoteNetworkQuality: mergedNetworkQuality
      });
    }, 5000);
  }
}

export async function joinMediaRoom(
  channelName: MediaRoom['channelName'],
  userRole: MediaUserRole,
  mode: SDK_MODE,
  forceFetchFromApi?: boolean
): Promise<MediaRoom> {
  trackerService.track('agora', 'shouldBeUsed', { isEnabled: true });
  const mediaRoom = await createMediaRoom(channelName, userRole, mode);

  subscribeMediaRoomEvents(mediaRoom);
  handleJoinChannel(mediaRoom, false, forceFetchFromApi);
  return mediaRoom;
}

export function shareScreen(mediaRoom: MediaRoom) {
  handleJoinChannel(mediaRoom, true);
}

export async function setRole(client: IAgoraRTCClient, channelName: string, userRole: MediaRoom['userRole']) {
  const currentUserId = store.state.user.currentUser?.id;

  if (!channelName || !currentUserId) {
    return;
  }
  if (currentUserHasStageRequest(channelName) && userRole === MediaUserRole.host) {
    updateOnStageRequest(currentUserId, 'COMPLETED');
  }
  await client.setClientRole(userRole);
  if (userRole === MediaUserRole.host) {
    syncUserUIDsWithBackend(client);
  }
}

export async function publishLocalTracks(client: IAgoraRTCClient, tracks: ILocalTrack[]) {
  try {
    const staticTracks = tracks.map(track => {
      return trackMap.get(track.getTrackId()) || track;
    });
    await client.publish(staticTracks);
    loggerService.warn('agora-publish-local-tracks');
  } catch (error) {
    loggerService.captureException(error as Error);
    throw error;
  }
}

export async function unpublishLocalTracks(client: IAgoraRTCClient, tracks: ILocalTrack[]): Promise<void> {
  try {
    tracks.forEach(track => {
      trackMap.delete(track.getTrackId());
    });
    await client.unpublish(tracks);
    loggerService.warn('agora-unpublish-local-tracks');
  } catch (error) {
    loggerService.captureException(error as Error);
    throw error;
  }
}

export async function exitMediaRoom(mediaRoom: MediaRoom): Promise<void> {
  try {
    unsubscribeMediaRoomEvents(mediaRoom);
    await mediaRoom?.client?.leave();
    await mediaRoom?.screenShareClient?.leave();
    analytics.track('exit_media_room', {
      ...getTrackingValues(mediaRoom)
    });
    toggleRemoteSignalTracking({ mediaRoom, enable: false });
    loggerService.warn('exited-media-room');
  } catch (error) {
    analytics.track('exit_media_room_failed', {
      ...getTrackingValues(mediaRoom),
      error: error
    });
    loggerService.captureException(error as Error);
    throw error;
  }
}

/* Agora error handling and metadata */

export function handleException(mediaRoom: MediaRoom, code: Number, uid: string, isCloudProxy: boolean) {
  if (code === 2001) {
    if (noOfLowAudioNotifications < NOTIFICATIONS_THRESHOLD) {
      NotificationService.toast(
        '@@translate@@ Microphone audio too low',
        "@@translate@@ We've detected a problem with your audio. We recommend trying to mute/unmute or use another device if the issue persists.",
        'warning',
        '',
        AGORA.AV_NOTIFICATION_TIMEOUT
      );
    }
    const [audioSnapshot] = getAudioTrackSnapshots(mediaRoom.channelName);
    loggerService.warn('agora-exception', {
      channelName: mediaRoom.channelName,
      userRole: mediaRoom.userRole,
      mode: mediaRoom.mode,
      count: noOfLowAudioNotifications,
      code,
      trackStatus: audioSnapshot?.trackStatus,
      readyState: audioSnapshot?.readyState
    });
    noOfLowAudioNotifications++;
    scheduleAudioRecovery(mediaRoom);
    return;
  }
  if (code === 4001) {
    noOfLowAudioNotifications--;
    loggerService.warn('agora-exception', {
      channelName: mediaRoom.channelName,
      userRole: mediaRoom.userRole,
      mode: mediaRoom.mode,
      count: noOfLowAudioNotifications,
      code
    });
    cancelAudioRecovery(mediaRoom.channelName);
    return;
  }

  if (code === 3003 || code === 4003) {
    noOfSendLowBitrateNotifications--;
    loggerService.warn('agora-exception', {
      channelName: mediaRoom.channelName,
      userRole: mediaRoom.userRole,
      mode: mediaRoom.mode,
      count: noOfSendLowBitrateNotifications,
      code
    });
  }

  if (code === 2003 || code === 1003) {
    if (noOfSendLowBitrateNotifications < NOTIFICATIONS_THRESHOLD) {
      NotificationService.toast(
        '@@translate@@ Video/Audio quality is low',
        "@@translate@@ We've detected that your video/audio bitrate is too low, which may affect video/audio quality. Please check your internet connection, close other bandwidth-intensive applications, or try switching to a different network.",
        'warning',
        '',
        AGORA.AV_NOTIFICATION_TIMEOUT
      );
    }
    loggerService.warn('agora-exception', {
      channelName: mediaRoom.channelName,
      userRole: mediaRoom.userRole,
      mode: mediaRoom.mode,
      count: noOfSendLowBitrateNotifications,
      code
    });
    noOfSendLowBitrateNotifications++;

    if (isCloudProxy) {
      NotificationService.toast(
        '@@translate@@ Audio/Video bitrate too low',
        '@@translate@@ Your network firewall settings are blocking the A/V streams. We recommend that you switch to a different network connection.',
        'warning'
      );
    }
    return;
  }

  if (code === 1005 && uid.includes(AGORA.UID_SUFFIX.SCREEN_STREAM)) {
    DialogService.show(
      GenericMessageDialog,
      {
        title: '@@translate@@ Error sharing screen',
        message: '@@translate@@ A network error occurred while sharing screen. Please check your network connection.',
        noCancel: true
      },
      {
        cancelOnClickOutside: false
      }
    );
    return;
  }
}

function logAgoraUserMetadata(
  currentEventUser: User | undefined,
  mediaUserRole: MediaUserRole,
  userSuffix: string,
  channelName: string
) {
  try {
    analytics.track('agora-user', {
      user_id: currentEventUser?.id,
      user_suffix: userSuffix.split('|')[1],
      channel_id: channelName.split('|')[0],
      channel_suffix: channelName.split('|')[1],
      user_type: currentEventUser?.eventUser?.event_role,
      email: currentEventUser?.email,
      media_user_role: mediaUserRole
    });
  } catch (error) {
    loggerService.warn('Error tracking agora user metadata', { error: classifyDiagnosticError(error, 'unknown') });
  }
}

/* Agora event subscriptions */

function subscribeMediaRoomEvents(mediaRoom: MediaRoom) {
  const currentUserId = store.state.user.currentUser?.id;
  const shouldSubscribe = (uid: string, mediaType?: string) =>
    shouldSubscribeToRemoteUser(currentUserId, uid, mediaType);
  const activeSpeakerState: ActiveSpeakerState = {
    lastActiveSpeakerId: null,
    lastSpokenAt: 0
  };
  const onScreenShareConnectionStateChange = (
    currentState: ConnectionState,
    previousState: ConnectionState,
    connectionDisconnectedReason?: ConnectionDisconnectedReason
  ) => handleScreenShareConnectionStateChange(mediaRoom, currentState, previousState, connectionDisconnectedReason);
  const onPeerConnectionStateChange = (
    currentPeerConnectionState: RTCPeerConnectionState,
    previousPeerConnectionState: RTCPeerConnectionState
  ) => handlePeerConnectionStateChange(mediaRoom, currentPeerConnectionState, previousPeerConnectionState);
  const onMediaRoomConnectionStateChange = (
    currentState: ConnectionState,
    previousState: ConnectionState,
    connectionDisconnectedReason?: ConnectionDisconnectedReason
  ) =>
    handleMediaRoomConnectionStateChange(
      mediaRoom,
      currentUserId,
      currentState,
      previousState,
      connectionDisconnectedReason
    );
  const onUserPublished = (user: IAgoraRTCRemoteUser, mediaType: ITrack['trackMediaType']) =>
    handleRemoteUserPublished(mediaRoom, shouldSubscribe, user, mediaType);
  const onUserUnpublished = (user: IAgoraRTCRemoteUser, mediaType: ITrack['trackMediaType']) =>
    handleRemoteUserUnpublished(mediaRoom, shouldSubscribe, user, mediaType);
  const onUserJoined = (user: IAgoraRTCRemoteUser) => handleRemoteUserJoined(mediaRoom, shouldSubscribe, user);
  const onUserLeft = (user: IAgoraRTCRemoteUser) => handleRemoteUserLeft(mediaRoom, shouldSubscribe, user);
  const onVolumeIndicator = (volumes: AgoraVolumeIndicator[]) =>
    handleVolumeIndicator(mediaRoom, currentUserId, activeSpeakerState, volumes);
  const onMediaRoomException = (exception: AgoraException) =>
    handleMediaRoomException(mediaRoom, currentUserId, exception);

  mediaRoom.screenShareClient.on('connection-state-change', onScreenShareConnectionStateChange);

  peerConnectionStartTimes.set(mediaRoom.channelName, Date.now());
  mediaRoom.client.on(AGORA_EVENTS.PEER_CONNECTION_STATE_CHANGE, onPeerConnectionStateChange);
  mediaRoom.client.on('connection-state-change', onMediaRoomConnectionStateChange);
  mediaRoom.client.on('user-published', onUserPublished);
  mediaRoom.client.on('user-unpublished', onUserUnpublished);
  mediaRoom.client.on('user-joined', onUserJoined);
  mediaRoom.client.on('user-left', onUserLeft);
  mediaRoom.client.on('network-quality', handleNetworkQuality);
  mediaRoom.client.on('join-fallback-to-proxy', handleJoinFallbackToProxy);
  mediaRoom.client.on('is-using-cloud-proxy', handleUsingCloudProxy);

  mediaRoom.client.enableAudioVolumeIndicator();
  mediaRoom.client.on('volume-indicator', onVolumeIndicator);
  mediaRoom.client.on('exception', onMediaRoomException);
  mediaRoom.screenShareClient.on('exception', handleScreenShareException);
}

function unsubscribeMediaRoomEvents(mediaRoom: MediaRoom) {
  mediaRoom?.client?.removeAllListeners();
  mediaRoom?.screenShareClient?.removeAllListeners();
  cancelAudioRecovery(mediaRoom.channelName);
}
